import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getConfig } from "../utils/config";
import { getStoryUrl } from "../utils/storyUrl";
import { getStoryReadyTime } from "../utils/storyReadyTime";
import { trackEvent } from "../utils/analytics";
import { decodeGuidToString, formatStoryCountdown } from "../helpers";
import "./SessionCompleted.scss";
import getStoryData from "../utils/storyData";
import { StoryData } from "../types/story";
import { getPlayerUid } from "../utils/cookies";

interface SessionCompletedBProps {
  isTestVariant?: boolean;
}

export const SessionCompletedB: React.FC<SessionCompletedBProps> = ({ isTestVariant = false }) => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("s");
  const sessionEndTime = searchParams.get("d");
  const storyReadyTime = sessionEndTime ? getStoryReadyTime(new Date(sessionEndTime)) : null;
  const [currentTime, setCurrentTime] = useState(() => new Date());
  const [storyData, setStoryData] = useState<StoryData | null>(null);

  const canViewStory = storyReadyTime ? currentTime >= storyReadyTime : false;
  const decodedSessionId = sessionId ? decodeGuidToString(sessionId) : null;

  const [storyUrl, setStoryUrl] = useState<string | null>(null);
  const playerUid: string | null = getPlayerUid();

  // const playerUid = "f4e1ba24-18cf-4aa4-a732-a2bb471c6d68"; //testing purposes only

  useEffect(() => {
    if (!sessionId) return;
    getStoryData(decodeGuidToString(sessionId)).then(setStoryData);
  }, [sessionId]);

  const actionReplayEventTypes = [5, 50, 51, 100, 155, 200, 201, 202, 400, 450];
  const associatedPlayerId = storyData?.Players?.find((i) => i.playerGuid === playerUid)?.id.toString();

  const associatedPlayerMedia = storyData?.Newsfeed?.find(
    (i) => String(i.players) === associatedPlayerId && actionReplayEventTypes.includes(i.type),
  );

  const firstActionReplay = storyData?.Newsfeed.find((i) => actionReplayEventTypes.includes(i.type));

  useEffect(() => {
    if (!storyReadyTime || canViewStory) {
      return;
    }

    const intervalId = window.setInterval(() => setCurrentTime(new Date()), 1000);
    return () => window.clearInterval(intervalId);
  }, [storyReadyTime, canViewStory]);

  useEffect(() => {
    if (canViewStory) {
      getConfig()
        .then((config) => {
          setStoryUrl(getStoryUrl(config.storyRegionDomain, decodedSessionId));
        })
        .catch(() => {}); // fall back to "story not ready" copy
    }
  }, [canViewStory, decodedSessionId]);

  const storyCountdown = storyReadyTime
    ? formatStoryCountdown(Math.max(0, storyReadyTime.getTime() - currentTime.getTime()))
    : null;

  const RE_VARIANT_VALUE = associatedPlayerMedia ? "B_playeractionreplay" : "C_generalactionreplay";

  useEffect(() => {
    if (!isTestVariant) return;
    trackEvent({
      gaEvent: "rapid_entry_test_impression",
      params: { re_variant: RE_VARIANT_VALUE },
    });
  }, [isTestVariant, RE_VARIANT_VALUE]);

  const ctaVariantSuffix = isTestVariant ? `&re_variant=${RE_VARIANT_VALUE}` : "";

  return (
    <div className="pageContainer">
      <div className="logoContainer">
        <img src={"./img/es-logo.svg"} width="110px" alt="Electric Shuffle" />
      </div>
      <div className="contentCard completeSession">
        <img src="./img/disco-ball.svg" className="discoBall" alt="Disco ball" />
        <div className="sessionCompletedContent">
          <div className="sessionCompletedTitle">Well Played!</div>
          {!storyUrl && (
            <>
              <div className="sessionCompletedText">
                We're putting together the best bits from your game in your own highlights reel. Check back
                when the countdown ends to see it!
                {storyCountdown && (
                  <span className="storyCountdown" aria-live="polite">
                    {storyCountdown}
                  </span>
                )}
              </div>
              <img src="./img/pucks-stars.svg" className="pucksStars" alt="Pucks and stars" />
            </>
          )}
          {storyUrl && (
            <>
              {!associatedPlayerMedia && !firstActionReplay && (
                <div className="sessionCompletedText">
                  The game is over but your fun doesn't stop there. Check out the game highlights in your
                  Story using the link below.
                </div>
              )}
              {associatedPlayerMedia ? (
                <>
                  <div className="sessionCompletedText">Here's a highlight from your game featuring you!</div>
                  <div className="firstImageContainer">
                    <video src={associatedPlayerMedia.video_url || ""} controls />
                  </div>
                </>
              ) : (
                firstActionReplay && (
                  <>
                    <div className="sessionCompletedText">Here's a highlight from your game!</div>
                    <div className="firstImageContainer">
                      <video src={firstActionReplay.video_url || ""} controls />
                    </div>
                  </>
                )
              )}
              <div className="storyButtons">
                <button
                  className="small"
                  onClick={() => {
                    trackEvent({
                      gaEvent: "session_complete_story",
                      clarityEvent: "session_complete_story",
                    });
                  }}
                >
                  <a
                    className="viewStoryLink"
                    href={`${storyUrl}?utm_source=rapid_entry&utm_medium=session_complete_story${ctaVariantSuffix}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Enjoy your story
                  </a>
                </button>
                <button
                  className="small cream"
                  onClick={() => {
                    trackEvent({
                      gaEvent: "session_complete_book",
                      clarityEvent: "session_complete_book",
                    });
                  }}
                >
                  <a
                    className="orange"
                    href={`https://electricshuffle.com/?utm_source=rapid_entry&utm_medium=session_complete${ctaVariantSuffix}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Book Next Round
                  </a>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      {!storyUrl && (
        <footer className="btn-container">
          <button className="button">
            <a href="https://www.electricshuffle.com/" target="_blank" rel="noopener noreferrer">
              Book Next Round
            </a>
          </button>
        </footer>
      )}
    </div>
  );
};
